/**
 * EIP-1559 unsigned-envelope builder for the web client.
 *
 * The gateway signs whatever `unsigned_payload` a caller supplies: it keccaks
 * the bytes, MPC-signs the digest, then folds the (r, s, recovery_id) back into
 * the same envelope and broadcasts it. It builds nothing from loose fields. So a
 * caller that wants a broadcastable EVM transaction must hand the gateway a
 * complete typed envelope, byte-for-byte the encoding the chain hashes:
 *
 *   0x02 || rlp([chain_id, nonce, max_priority_fee_per_gas, max_fee_per_gas,
 *                gas_limit, to, value, data, access_list])
 *
 * This module produces exactly that envelope from a swap quote's transaction
 * fields plus a nonce. The field order, the empty access list (0xc0), and the
 * 0x02 type prefix mirror the gateway's own Eip1559Transaction encoder and the
 * mobile app's EvmTxBuilder, so a payload this builds hashes to the same digest
 * the gateway will sign and the same one the destination network will recover.
 *
 * Encoding is done with hand-rolled minimal RLP over bytes. It is small,
 * dependency-free, and every quantity is encoded in the canonical minimal-length
 * big-endian form RLP requires (no leading zero bytes, zero encodes as the empty
 * string 0x80). BigInt is used throughout so a full-width uint128 value or fee
 * never touches floating point.
 *
 * Scope note: this is used only by the swap execution path, which is gated to
 * EVM testnet, cross-chain, native-in swaps in this build. The builder itself is
 * chain-agnostic and value-agnostic; the gating lives in the caller.
 */

/** The EIP-1559 typed-transaction prefix byte. */
const EIP1559_TYPE = 0x02;

/** The RLP encoding of an empty list, used for the (always empty) access list. */
const EMPTY_LIST = 0xc0;

/**
 * The transaction fields needed to build an unsigned EIP-1559 envelope. Every
 * numeric field is a non-negative integer; addresses and calldata are hex.
 */
export interface Eip1559Fields {
  /** EVM chain id. Bound into the signature and used for broadcast routing. */
  chainId: number;
  /** Sender account nonce on the origin chain. */
  nonce: bigint;
  /** Max priority fee per gas, in Wei. */
  maxPriorityFeePerGas: bigint;
  /** Max total fee per gas, in Wei. */
  maxFeePerGas: bigint;
  /** Gas limit. */
  gasLimit: bigint;
  /** Destination address (the DEX/bridge router), 20 bytes, 0x-prefixed hex. */
  to: string;
  /** Value to send, in Wei (the native-in amount for a native swap). */
  value: bigint;
  /** Calldata (the swap calldata), 0x-prefixed hex; empty allowed. */
  data: string;
}

/**
 * Thrown when a field cannot be encoded into a valid EIP-1559 envelope, for
 * example a malformed address or a negative quantity. Carries a plain message
 * the caller can surface; a build failure must block signing rather than emit a
 * malformed payload the gateway would sign into an un-broadcastable transaction.
 */
export class EnvelopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvelopeError';
  }
}

/**
 * Build the unsigned EIP-1559 typed envelope for a transaction, hex-encoded with
 * a `0x` prefix, ready to pass as the gateway's `unsigned_payload`.
 *
 * @param fields The transaction fields plus the resolved nonce.
 * @returns `0x02...` hex string: the typed envelope the gateway will sign.
 * @throws {EnvelopeError} When a field is malformed or out of range.
 */
export function buildEip1559Envelope(fields: Eip1559Fields): string {
  const to = hexToBytes(fields.to);
  if (to.length !== 20) {
    throw new EnvelopeError(`destination address must be 20 bytes, got ${to.length}`);
  }
  const data = fields.data && fields.data !== '0x' ? hexToBytes(fields.data) : new Uint8Array(0);

  const items: Uint8Array[] = [
    rlpUint(BigInt(fields.chainId)),
    rlpUint(fields.nonce),
    rlpUint(fields.maxPriorityFeePerGas),
    rlpUint(fields.maxFeePerGas),
    rlpUint(fields.gasLimit),
    rlpBytes(to),
    rlpUint(fields.value),
    rlpBytes(data),
    Uint8Array.of(EMPTY_LIST),
  ];

  const list = rlpList(items);
  const envelope = new Uint8Array(1 + list.length);
  envelope[0] = EIP1559_TYPE;
  envelope.set(list, 1);
  return `0x${bytesToHex(envelope)}`;
}

/**
 * Parse a decimal or hex integer string from a quote into a non-negative BigInt.
 * Providers return amounts and fees as decimal strings; some fields (calldata,
 * addresses) are hex, but numeric fields are decimal. An empty or missing value
 * yields the supplied default so an omitted optional gas field can fall back.
 *
 * @param value The string from the quote, or undefined.
 * @param fallback The value to use when the input is empty or undefined.
 * @returns The parsed non-negative BigInt.
 * @throws {EnvelopeError} When the value is present but not a non-negative integer.
 */
export function parseQuantity(value: string | undefined, fallback: bigint): bigint {
  if (value === undefined || value.trim() === '') return fallback;
  const trimmed = value.trim();
  let parsed: bigint;
  try {
    // BigInt accepts a leading 0x for hex and a bare decimal string; both are
    // shapes providers use. A malformed value throws, which is caught below.
    parsed = BigInt(trimmed);
  } catch {
    throw new EnvelopeError(`could not parse a numeric quote field: ${trimmed}`);
  }
  if (parsed < 0n) {
    throw new EnvelopeError(`quote field must be non-negative: ${trimmed}`);
  }
  return parsed;
}

/**
 * RLP-encode a non-negative integer as a minimal-length big-endian byte string.
 * Zero encodes as the empty string (RLP 0x80), and no value carries a leading
 * zero byte, which is the canonical form the chain expects.
 *
 * @param value The non-negative integer.
 * @returns The RLP encoding.
 */
function rlpUint(value: bigint): Uint8Array {
  if (value < 0n) throw new EnvelopeError('cannot RLP-encode a negative integer');
  if (value === 0n) return Uint8Array.of(0x80);
  return rlpBytes(bigIntToMinimalBytes(value));
}

/**
 * RLP-encode a byte string with the correct single-byte, short, or long header.
 *
 * @param bytes The raw bytes.
 * @returns The RLP encoding.
 */
function rlpBytes(bytes: Uint8Array): Uint8Array {
  // A single byte below 0x80 is its own encoding.
  if (bytes.length === 1 && bytes[0] < 0x80) return bytes;
  return concat(rlpLengthHeader(bytes.length, 0x80, 0xb7), bytes);
}

/**
 * RLP-encode a list from its already-encoded items.
 *
 * @param items The RLP-encoded list elements.
 * @returns The RLP encoding of the list.
 */
function rlpList(items: Uint8Array[]): Uint8Array {
  const body = concat(...items);
  return concat(rlpLengthHeader(body.length, 0xc0, 0xf7), body);
}

/**
 * Build the RLP length header for a string or list. Lengths under 56 use the
 * short form (base + length); longer payloads use the long form (base + 55 +
 * length-of-length, followed by the big-endian length).
 *
 * @param length The payload length.
 * @param shortBase 0x80 for strings, 0xc0 for lists.
 * @param longBase 0xb7 for strings, 0xf7 for lists.
 * @returns The header bytes.
 */
function rlpLengthHeader(length: number, shortBase: number, longBase: number): Uint8Array {
  if (length < 56) return Uint8Array.of(shortBase + length);
  const lenBytes = bigIntToMinimalBytes(BigInt(length));
  return concat(Uint8Array.of(longBase + lenBytes.length), lenBytes);
}

/**
 * Convert a positive BigInt to its minimal big-endian byte representation, with
 * no leading zero byte. Caller guarantees value > 0.
 *
 * @param value The positive integer.
 * @returns The minimal big-endian bytes.
 */
function bigIntToMinimalBytes(value: bigint): Uint8Array {
  let hex = value.toString(16);
  if (hex.length % 2 === 1) hex = `0${hex}`;
  return hexDigitsToBytes(hex);
}

/**
 * Decode a `0x`-prefixed or bare hex string into bytes. Rejects odd-length or
 * non-hex input so a malformed address or calldata cannot silently truncate.
 *
 * @param hex The hex string, with or without a `0x` prefix.
 * @returns The decoded bytes.
 * @throws {EnvelopeError} When the input is not clean even-length hex.
 */
function hexToBytes(hex: string): Uint8Array {
  const body = hex.startsWith('0x') || hex.startsWith('0X') ? hex.slice(2) : hex;
  if (body.length % 2 !== 0) {
    throw new EnvelopeError(`hex value has an odd length: ${hex}`);
  }
  if (body.length > 0 && !/^[0-9a-fA-F]+$/.test(body)) {
    throw new EnvelopeError(`hex value has non-hex characters: ${hex}`);
  }
  return hexDigitsToBytes(body);
}

/**
 * Convert clean even-length hex digits (no prefix, validated by the caller) to
 * bytes.
 *
 * @param body The hex digits.
 * @returns The decoded bytes.
 */
function hexDigitsToBytes(body: string): Uint8Array {
  const out = new Uint8Array(body.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(body.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * Encode bytes as a lowercase hex string with no prefix.
 *
 * @param bytes The bytes.
 * @returns The hex string.
 */
function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * Concatenate byte arrays into one.
 *
 * @param parts The arrays to join.
 * @returns The concatenation.
 */
function concat(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}
